import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Sparkles } from 'lucide-react';
import clsx from 'clsx';
import { boostApi } from '@/lib/api';

const CITIES = [
  { id: 1, name: 'Toronto' },
  { id: 2, name: 'Montréal' },
  { id: 3, name: 'Vancouver' },
] as const;

export function BoostsPage() {
  const [cityId, setCityId] = useState<number>(CITIES[0].id);

  const packagesQ = useQuery({
    queryKey: ['boost', 'packages', cityId],
    queryFn: () => boostApi.packages(cityId),
  });

  const promotionsQ = useQuery({ queryKey: ['boost', 'by-promotion'], queryFn: () => boostApi.byPromotion() });
  const activeRows = (promotionsQ.data ?? []).filter((p) => p.status === 'active');

  return (
    <div className="mx-auto max-w-6xl space-y-8">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">Boosts</h1>
          <p className="mt-1 text-text-secondary">Promote your venue, event, or resident DJ.</p>
        </div>
        <select
          value={cityId}
          onChange={(e) => setCityId(Number(e.target.value))}
          className="input pr-9 w-auto"
        >
          {CITIES.map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
      </header>

      <section>
        <h2 className="mb-4 text-lg font-bold">Available packages</h2>
        {packagesQ.isLoading ? (
          <p className="text-text-muted">Loading…</p>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {(packagesQ.data ?? []).map((p) => (
              <div
                key={p.id}
                className={clsx(
                  'card card-hover relative flex flex-col gap-3',
                  !p.is_active && 'opacity-50',
                )}
              >
                <div className="flex items-center gap-2 text-featured">
                  <Sparkles size={16} />
                  <span className="text-xs font-semibold uppercase tracking-wide">{p.name}</span>
                </div>
                <p className="text-2xl font-bold">
                  {/* Backend Postgres NUMERIC döner — string olarak gelir.
                      Number(...) hem string hem number için güvenli. */}
                  ${Number(p.price_cad ?? 0).toFixed(0)}
                  <span className="ml-1 text-sm font-normal text-text-muted">/ {p.duration_hours}h</span>
                </p>
                {p.description ? (
                  <p className="text-sm text-text-secondary">{p.description}</p>
                ) : null}
                <button
                  type="button"
                  disabled={!p.is_active}
                  className="btn-primary mt-2"
                  onClick={() =>
                    window.alert('Stripe Checkout flow — Faz 6.1 (next iteration)')
                  }
                >
                  Buy boost
                </button>
              </div>
            ))}
            {(packagesQ.data ?? []).length === 0 ? (
              <div className="card text-center text-text-muted">
                No packages configured for this city yet.
              </div>
            ) : null}
          </div>
        )}
      </section>

      <section>
        <h2 className="mb-4 text-lg font-bold">Active boosts ({activeRows.length})</h2>
        {activeRows.length === 0 ? (
          <div className="card text-center text-text-muted">
            No active boosts right now.
          </div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            {activeRows.map((r) => (
              <div key={r.id} className="card">
                <div className="text-xs uppercase tracking-wide text-text-muted">{r.slot}</div>
                <div className="mt-1 font-bold">{r.package}</div>
                <div className="mt-3 grid grid-cols-3 gap-2 text-sm">
                  <Stat label="Impressions" value={Number(r.impressions ?? 0).toLocaleString()} />
                  <Stat label="Clicks" value={Number(r.clicks ?? 0).toLocaleString()} />
                  <Stat label="CTR" value={`${(Number(r.ctr ?? 0) * 100).toFixed(2)}%`} />
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-text-muted">{label}</div>
      <div className="font-semibold tabular-nums">{value}</div>
    </div>
  );
}
